import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AnalyticsService, InterpolationService, TableRow, TimeSeries } from 'map-wald';
import { VectorLayerDescriptor } from 'map-wald-leaflet';
import { TimeSeriesService } from 'terrakio';
import { map, Observable, of } from 'rxjs';
import { CacheService } from '../cache.service';
import { ChartEntry, ChartSeries } from '../chart/chart.component';
import { FlattenedLayerDescriptor, LayerDescriptor, OverlaySelection, TimePeriod } from '../data';
import { PointDataService } from '../point-data.service';
import { ZonalService } from '../zonal.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CM_ANNUAL, CM_ANNUAL_FILTERED, CM_DEVIATION, CM_DEVIATION_ANNUAL_PC, CM_NORMAL, CM_YR_ON_YR, CM_YR_ON_YR_CUMUL, MultiYearTimeseriesChartComponent } from '../multi-year-timeseries-chart/multi-year-timeseries-chart.component';
import * as R from 'ramda';

export const CUSTOM_POLYGON='custom-drawn';

export type PolygonMode = 'point' | 'predefined' | 'draw';
export const ALL_POLYGON_MODES = ['predefined','point','draw'] as PolygonMode[];

const EXPANDED_CHART_FRACTION_HEIGHT = 0.6;
const BASE_CHART_DESCIPTIONS: {[key:string]:string} = {
  [CM_NORMAL]:'monthly time series',
}

const GLOBAL_AVERAGE = 'Global land area';
const CHART_PROMPTS = {
  predefined: 'Select a region',
  draw: 'Draw a region',
  point: 'Select a point'
};

@Component({
  selector: 'app-chart-panel',
  templateUrl: './chart-panel.component.html',
  styleUrls: ['./chart-panel.component.scss']
})
export class ChartPanelComponent implements OnInit, OnChanges {
  @Input() chartMode: string = CM_ANNUAL;
  @Input() selectionMode: PolygonMode = 'predefined';

  @Input() validModes = ALL_POLYGON_MODES;
  @Input() overlaySelection: OverlaySelection;
  @Input() timePeriod: TimePeriod;
  @Input() vectorLayer: VectorLayerDescriptor;
  @Input() selectedFeature: GeoJSON.Feature<GeoJSON.GeometryObject>;
  @Input() allowExpand = true;
  @Input() expanded = false;

  @Output() selectionModeChange = new EventEmitter<PolygonMode>();
  @Output() chartModeChange = new EventEmitter<string>();
  @Output() chartCleared = new EventEmitter<any>();
  @Output() loadingStarted = new EventEmitter<any>();
  @Output() loadingFinished = new EventEmitter<boolean>();
  @Output() sizeToggled = new EventEmitter<boolean>();

  @ViewChild('theChart',{static:false}) theChart:MultiYearTimeseriesChartComponent;
  // @ViewChild('theChartPopout',{static:false}) theChartPopout:MultiYearTimeseriesChartComponent;
  // get theChart():MultiYearTimeseriesChartComponent{
  //   return this.current?this.theChartPopout:this.theChartInline;
  // }
  // @ViewChild('popupChart',{static:false}) popupChart:any;
  // current:NgbModalRef;

  allChartModes = [CM_NORMAL,CM_ANNUAL,CM_ANNUAL_FILTERED,CM_DEVIATION,CM_DEVIATION_ANNUAL_PC,CM_YR_ON_YR,CM_YR_ON_YR_CUMUL];
  chartModes = this.allChartModes.slice();
  allPolygonModes = ALL_POLYGON_MODES;

  rawChartData: ChartSeries;
  focusYear = -1;
  chartRequestNumber = 0;
  chartPolygonLabel: string;
  chartDescription: string;
  chartPrompt = CHART_PROMPTS;
  chartError: string;
  dataTimestep: string;
  validSelectionModes: {[key in PolygonMode]:boolean} = {
    draw:true,
    point:true,
    predefined:true
  };

  shortLabel:any = {  }

  constructor(private http: CacheService,
              private zonal: ZonalService,
              private pointData: PointDataService,
              private customTimeSeries: TimeSeriesService,
              private analytics: AnalyticsService,
              private modalService: NgbModal) {
    Object.keys(CHART_PROMPTS).forEach(k=>{
      this.shortLabel[k] = (CHART_PROMPTS as any)[k].replace(' a ',' ');
    })
  }

  loadingSucceeded(): void {
    this.loadingFinished.emit(true);
  }

  loadingFailed(): void {
    this.loadingFinished.emit(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.validModes){
      this.setupSelectionModes();
    }

    if(changes.overlaySelection){
      this.setChartModes();
    }

    if(changes.selectedFeature||changes.overlaySelection){
      this.selectedFeatureChanged();
    }
    if(changes.timePeriod){
      this.setDate();
    }
  }

  setChartModes(): void {
    const variant = this.overlaySelection?.flattened;
    const styles = variant?.gridStyles || variant?.zonalStyles || variant?.pointStyles;
    const hasNegative = styles?.classes ? R.any(c=>c<0,styles.classes) : false;
    this.chartModes = this.allChartModes.slice();
    if(hasNegative){
      this.chartModes = this.chartModes.filter(m=>m!==CM_DEVIATION_ANNUAL_PC);
    }
  }

  setupSelectionModes() {
    ALL_POLYGON_MODES.forEach(m=>this.validSelectionModes[m]=false);
    (this.validModes||[]).forEach(m=>this.validSelectionModes[m]=true);
    if(!this.validSelectionModes[this.selectionMode]){
      this.selectionMode = 'point';
      this.featureSelectionModeChanged();
    }
  }

  ngOnInit(): void {
  }

  setLayer() {
    if((this.overlaySelection.layer?.type!=='grid')&&this.selectedFeature){
      this.unsetFeature();
    }
    this.createChart();
  }

  unsetFeature() {
    this.clearChart();
    this.selectedFeature = null;
  }

  setDate() {
    this.focusYear = this.timePeriod?.to?.getUTCFullYear();
  }

  variantChanged():void{
    this.createChart();
  }

  selectedFeatureChanged() {
    this.clearChart();
    this.createChart();
  }

  createChart() {
    if(this.selectedFeature?.geometry?.type==='Point'){
      this.chartPointTimeSeries();
    } else {
      this.chartPolygonTimeSeries();
    }
  }

  chartPointTimeSeries() {
    const flat = this.overlaySelection.flattened;
    if(flat.type!=='point'){
      return;
    }

    this.loadingStarted.emit(null);
    this.pointData.getTimeSeries(flat,this.selectedFeature).subscribe(timeseries=>{
      if(!timeseries){
        this.clearChart();
        this.loadingFailed();
        return;
      }

      const chartData:ChartEntry[] = timeseries.dates.map((d,i)=>{
        return {
          date:d,
          value:timeseries.values[i]
        };
      }).filter(row=>(row.value!==null)&&!isNaN(row.value));
      this.setupChart(flat,chartData);
      const labelTemplate = flat.chartLabel || this.selectedFeature.properties.chartLabel;
      if(labelTemplate){
        this.chartPolygonLabel = InterpolationService.interpolate(labelTemplate,this.selectedFeature?.properties);
      } else {
        this.chartPolygonLabel=null;
      }
      this.dataTimestep = 'daily';
      this.loadingSucceeded();
    });
  }

  buildChartDescription() {
    if(!this.rawChartData?.title){
      this.chartDescription = null;
      return;
    }
    const base = BASE_CHART_DESCIPTIONS[this.chartMode] || this.chartMode;
    this.chartDescription = `Charting: ${this.rawChartData?.title} as ${base}`;
  }

  private chartPolygonTimeSeries() {
    this.chartRequestNumber++;
    if(!this.overlaySelection?.flattened){
      return;
    }
    // if(!this.selectedFeature){
    //   return;
    // }

    const currentRequest = this.chartRequestNumber;
    const layer = this.overlaySelection.flattened;
    const havePrecomputed = layer.zonal && (this.selectedFeature?.properties?.source !== CUSTOM_POLYGON);

    if (!layer.polygonDrill&&!havePrecomputed) {
      return;
    }

    this.loadingStarted.emit(null);
    const data$ = havePrecomputed ?
      this.getPrecomputedTimeSeries(this.selectedFeature):
      this.getTimeSeriesForCustomPolygon(this.selectedFeature);

      data$.subscribe({
        next:data => {
      if (!data?.length) {
        return;
      }

      if(this.chartRequestNumber!==currentRequest){
        return;
      }

      if(layer!==this.overlaySelection.flattened){
        return;
      }

      data = data.filter(rec => rec.value !== -9999).map(rec => {
        let theDate: Date;
        if (rec.date?.getUTCFullYear) {
          theDate = rec.date;
        } else {
          const dString = '' + rec.date;
          theDate = new Date(+dString.slice(0, 4), +dString.slice(4, 6) - 1, +dString.slice(6, 8));
        }

        const result: ChartEntry = {
          date: theDate,
          value: rec.value
        };
        return result;
      });
      // data = data.reverse();
      this.chartPolygonLabel = this.makePolygonChartLabel();
      this.setupChart(this.overlaySelection.flattened, data as ChartEntry[]);
      this.dataTimestep = 'monthly';
      this.loadingSucceeded();
    }, error:err => {
      this.loadingFailed();
      this.chartError = "Could not load time series for " + this.makePolygonChartLabel();
    }});
  }

  makePolygonChartLabel() {
    if(!this.selectedFeature){
      return GLOBAL_AVERAGE;
    } else if ((this.selectedFeature?.properties?.source!==CUSTOM_POLYGON)&&this.vectorLayer.label) {
      return InterpolationService.interpolate(this.vectorLayer.label, this.selectedFeature.properties);
    } else

    return this.selectedFeature.properties.chartLabel;
  }

  userClearedChart(): void {
    this.unsetFeature();
    this.chartCleared.emit(null);
    this.createChart();
  }

  clearChart(): void {
    this.chartError = null;
    this.setupChart(null,null);
  }

  setupChart(layer: FlattenedLayerDescriptor, chartData: ChartEntry[]): void{
    if(!layer||!chartData) {
      this.chartPolygonLabel=null;
      this.rawChartData = null;
      this.buildChartDescription();
      return;
    }

    let title = layer.variantLabel || layer.label;
    // if(layer.variantLabel){
    //   title += '-'+layer.variantLabel;
    // }

    this.rawChartData = {
      title:title,
      units: layer.units,
      data:chartData
    };
    this.buildChartDescription();
  }

  getPrecomputedTimeSeries(geoJSON:any): Observable<TableRow[]> {
    let ts$: Observable<TimeSeries>;
    if(geoJSON){
      let keyField = this.vectorLayer.keyField;
      if(!keyField&&this.vectorLayer.tileLayers?.length){
        keyField = this.vectorLayer.tileLayers[0].keyField;
      }
      ts$ = this.zonal.getTimeSeries(this.overlaySelection.flattened,
                                     this.vectorLayer.zonal,
                                     geoJSON?.properties?.[keyField]);
    } else {
      ts$ = this.zonal.getGlobalTimeSeries(this.overlaySelection.flattened);
    }

    return ts$.pipe(
      map(ts=>{
        return ts.dates.map((d,i)=>{
          return {
            date:d,
            value:ts.values[i]
          };
        });
      })
    );
  }

  getTimeSeriesForCustomPolygon(geoJSON: any): Observable<TableRow[]> {
    if(!this.overlaySelection.layer.polygonDrill){
      return of(null);
    }

    const currentSelection = this.chartRequestNumber;

    const cfg = this.overlaySelection.flattened;
    // let v = cfg.variable || cfg.shortVariable;
    // v = `${cfg.collection}.${v}`;

    return this.customTimeSeries.forPolygon(geoJSON,cfg).pipe(
      map(rows=>{
        if(this.chartRequestNumber !== currentSelection) {
          return null;
        }
        return rows;
      }),
      map(rows=>{
        if(cfg.timePeriod){
          return rows.filter(row=>cfg.timePeriod.contains(row.date));
        }
        return rows;
      }),
      map(rows=>{
        if(!rows||(cfg.scale===1.0)){
          return rows;
        }
        return rows.map(row=>{
          return {
            ...row,
            value:row.value*cfg.scale
          };
        });
      })
    );

    // geoJSON = cleanFeature(geoJSON);
    // const result$ = this.http.post(this.overlaySelection.layer.polygonDrill,{
    //   feature:{...geoJSON,properties:{}},
    //   output: 'csv',
    //   name:cfg.collection,
    //   products:[v],
    //   in_crs:'epsg:4326',
    //   out_crs:'epsg:4326',
    //   resolution: 0.1,
    //   expr:`mean:space(${v})`
    // }, {
    //   responseType:'text'
    // }).pipe(
    //   map(res=>{
    //     if(this.chartRequestNumber !== currentSelection) {
    //       return null;
    //     }

    //     const data = parseCSV(res,{
    //       columns:DATA_COLUMNS,
    //       headerRows:1
    //     });
    //     return data;
    //   }));
    // return result$;
  }

  featureSelectionModeChanged(): void {
    this.selectionModeChange.emit(this.selectionMode);
  }

  expand(): void {
    this.sizeToggled.emit(!this.expanded);
    // this.showExpandedChart();
  }

  // showExpandedChart(): void {
  //   this.expandedChartHeight = EXPANDED_CHART_FRACTION_HEIGHT * window.innerHeight;
  //   console.log('opening chart');
  //   this.current = this.modalService.open(this.popupChart,{
  //     size:'lg',
  //     windowClass:'expanded-chart'
  //   });
  // }

  close(): void {

  }

  dismiss(): void {

  }

  downloadData() {
    this.theChart.downloadData();
  }

  downloadChart() {
    this.theChart.downloadImage();
  }

  _chartModeChanged(): void {
    this.buildChartDescription();
    // this.configureChartMode();
    this.chartModeChange.emit(this.chartMode);
  }

}

